/**
 * リポジトリファクトリ
 * 適切なリポジトリインスタンスを提供するファクトリ
 */

import { IMapRepository, INodeRepository, IEdgeRepository, ITranscriptRepository } from './interfaces';
// Mock リポジトリのインポート
import { MockMapRepository } from './mock/MockMapRepository';
import { MockNodeRepository } from './mock/MockNodeRepository';
import { MockEdgeRepository } from './mock/MockEdgeRepository';
import { MockTranscriptRepository } from './mock/MockTranscriptRepository';
// Firebase リポジトリのインポート
import { FirebaseMapRepository } from './firebase/FirebaseMapRepository';
import { FirebaseNodeRepository } from './firebase/FirebaseNodeRepository';
import { FirebaseEdgeRepository } from './firebase/FirebaseEdgeRepository';
import { FirebaseTranscriptRepository } from './firebase/FirebaseTranscriptRepository';

// リポジトリ種別
export type RepositoryType = 'mock' | 'firebase';

/**
 * リポジトリファクトリクラス
 * 適切なリポジトリインスタンスを生成するファクトリパターン実装
 */
export class RepositoryFactory {
  private static instance: RepositoryFactory;
  private repositoryType: RepositoryType = 'firebase'; // デフォルトをFirebaseに変更
  
  // モックリポジトリのシングルトンインスタンス
  private mockMapRepository: MockMapRepository | null = null;
  private mockNodeRepository: MockNodeRepository | null = null;
  private mockEdgeRepository: MockEdgeRepository | null = null;
  private mockTranscriptRepository: MockTranscriptRepository | null = null;
  
  // Firebase リポジトリのシングルトンインスタンス
  private firebaseMapRepository: FirebaseMapRepository | null = null;
  private firebaseNodeRepository: FirebaseNodeRepository | null = null;
  private firebaseEdgeRepository: FirebaseEdgeRepository | null = null;
  private firebaseTranscriptRepository: FirebaseTranscriptRepository | null = null;
  
  /**
   * シングルトンパターンのためのプライベートコンストラクタ
   */
  private constructor() {
    // 環境変数からリポジトリタイプを決定することもできる
    this.repositoryType = process.env.NEXT_PUBLIC_REPOSITORY_TYPE as RepositoryType || 'firebase';
    
    // 開発環境では環境変数に基づいて選択
    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_USE_MOCK === 'true') {
      this.repositoryType = 'mock';
    }
  }
  
  /**
   * シングルトンインスタンスを取得
   */
  public static getInstance(): RepositoryFactory {
    if (!RepositoryFactory.instance) {
      RepositoryFactory.instance = new RepositoryFactory();
    }
    
    return RepositoryFactory.instance;
  }
  
  /**
   * リポジトリタイプを設定（主にテスト用）
   */
  public setRepositoryType(type: RepositoryType): void {
    this.repositoryType = type;
    
    // タイプ変更時にリポジトリインスタンスをリセット
    this.mockMapRepository = null;
    this.mockNodeRepository = null;
    this.mockEdgeRepository = null;
    this.mockTranscriptRepository = null;
    
    this.firebaseMapRepository = null;
    this.firebaseNodeRepository = null;
    this.firebaseEdgeRepository = null;
    this.firebaseTranscriptRepository = null;
  }
  
  /**
   * 現在のリポジトリタイプを取得
   */
  public getRepositoryType(): RepositoryType {
    return this.repositoryType;
  }
  
  /**
   * マップリポジトリを取得
   */
  public getMapRepository(): IMapRepository {
    if (this.repositoryType === 'mock') {
      if (!this.mockMapRepository) {
        this.mockMapRepository = new MockMapRepository();
      }
      return this.mockMapRepository;
    } else if (this.repositoryType === 'firebase') {
      if (!this.firebaseMapRepository) {
        this.firebaseMapRepository = new FirebaseMapRepository();
      }
      return this.firebaseMapRepository;
    }
    
    throw new Error(`Repository type ${this.repositoryType} not implemented`);
  }
  
  /**
   * ノードリポジトリを取得
   */
  public getNodeRepository(): INodeRepository {
    if (this.repositoryType === 'mock') {
      if (!this.mockNodeRepository) {
        const mapRepo = this.getMapRepository() as MockMapRepository;
        this.mockNodeRepository = new MockNodeRepository(mapRepo);
      }
      return this.mockNodeRepository;
    } else if (this.repositoryType === 'firebase') {
      if (!this.firebaseNodeRepository) {
        const mapRepo = this.getMapRepository() as FirebaseMapRepository;
        this.firebaseNodeRepository = new FirebaseNodeRepository(mapRepo);
      }
      return this.firebaseNodeRepository;
    }
    
    throw new Error(`Repository type ${this.repositoryType} not implemented`);
  }
  
  /**
   * エッジリポジトリを取得
   */
  public getEdgeRepository(): IEdgeRepository {
    if (this.repositoryType === 'mock') {
      if (!this.mockEdgeRepository) {
        const mapRepo = this.getMapRepository() as MockMapRepository;
        this.mockEdgeRepository = new MockEdgeRepository(mapRepo);
      }
      return this.mockEdgeRepository;
    } else if (this.repositoryType === 'firebase') {
      if (!this.firebaseEdgeRepository) {
        const mapRepo = this.getMapRepository() as FirebaseMapRepository;
        this.firebaseEdgeRepository = new FirebaseEdgeRepository(mapRepo);
      }
      return this.firebaseEdgeRepository;
    }
    
    throw new Error(`Repository type ${this.repositoryType} not implemented`);
  }
  
  /**
   * トランスクリプトリポジトリを取得
   */
  public getTranscriptRepository(): ITranscriptRepository {
    if (this.repositoryType === 'mock') {
      if (!this.mockTranscriptRepository) {
        const mapRepo = this.getMapRepository() as MockMapRepository;
        this.mockTranscriptRepository = new MockTranscriptRepository(mapRepo);
      }
      return this.mockTranscriptRepository;
    } else if (this.repositoryType === 'firebase') {
      if (!this.firebaseTranscriptRepository) {
        const mapRepo = this.getMapRepository() as FirebaseMapRepository;
        this.firebaseTranscriptRepository = new FirebaseTranscriptRepository(mapRepo);
      }
      return this.firebaseTranscriptRepository;
    }
    
    throw new Error(`Repository type ${this.repositoryType} not implemented`);
  }
  
  /**
   * テスト用: モックリポジトリにデータを注入
   */
  public injectMockData(data: any): void {
    if (this.repositoryType !== 'mock') {
      throw new Error('Data injection is only available for mock repositories');
    }
    
    if (!this.mockMapRepository) {
      this.mockMapRepository = new MockMapRepository();
    }
    
    this.mockMapRepository.injectData(data);
  }
  
  /**
   * テスト用: モックリポジトリをリセット
   */
  public resetMockRepositories(): void {
    if (this.mockMapRepository) {
      this.mockMapRepository.resetWithSampleData();
    }
  }
}

/**
 * リポジトリファクトリのシングルトンインスタンスを取得する簡易関数
 */
export function getRepositoryFactory(): RepositoryFactory {
  return RepositoryFactory.getInstance();
}